import useOrganizationFlags from '@/modules/auth/composables/use-organization-flags'
import useUser from '@/modules/auth/composables/use-user'
import { useLocalStorage } from '@vueuse/core'
import { computed, ref } from 'vue'

export type DesignVersion = 'v1' | 'v2'

const SESSION_DISMISSED_KEY = 'design_migration_banner_dismissed_session'

const localOverride = useLocalStorage<DesignVersion | null>('design_version', null)
const bannerDismissed = useLocalStorage('design_migration_banner_dismissed', false)
// Plain sessionStorage rather than useSessionStorage: this module is pulled in almost everywhere, and
// a second @vueuse/core import breaks every spec that mocks that package with only the exports it uses.
const bannerDismissedThisSession = ref(sessionStorage.getItem(SESSION_DISMISSED_KEY) === 'true')

export const MIGRATION_BANNER_HEIGHT = '40px'

const resolveIsV2 = () => localOverride.value !== 'v1'

export function useDesignVersion() {
    const { hasFlag } = useOrganizationFlags()
    const { organization } = useUser()

    const isV2 = computed(resolveIsV2)
    const isMigrationPending = computed(() => !!organization.value && !hasFlag('design_v2'))

    const isDismissed = computed(() => isV2.value ? bannerDismissed.value : bannerDismissedThisSession.value)

    return {
        isV2,
        isMigrationPending,
        isBannerVisible: computed(() => isMigrationPending.value && !isDismissed.value),
        dismissBanner: () => {
            if (isV2.value) {
                bannerDismissed.value = true
            } else {
                sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true')
                bannerDismissedThisSession.value = true
            }
        },
    }
}

/**
 * Non-reactive read — safe to call outside component setup (e.g. router).
 * Returns the current flag value at call time.
 */
export function getIsV2(): boolean {
    return resolveIsV2()
}

export function setDesignVersion(version: DesignVersion) {
    if (version === 'v1') {
        sessionStorage.removeItem(SESSION_DISMISSED_KEY)
        bannerDismissedThisSession.value = false
    }

    localOverride.value = version
    window.location.reload()
}
